import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { V1ListModelFamilyRequest, V1ModelType } from '@blk/portfolio-security-server/models';
import { ModelsTransformUtil } from '@ddt-web/common';
import { ERROR_SEVERITY } from '@ddt-web/config';
import { InjectConfig, LocaleUtilResolverService, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { DateFormatter, PasDataServiceApiConstants, PasRcResolverService } from '@ddt-web/data-service';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { ErrorObject, PasModelFamiliesError } from '@ddt-web/error-handler';
import { DateUtil, InjectQueryParam } from '@ddt-web/generic-utils';
import { mapModelsFamiliesResponse, PasApiParamUtil, SecurityResolutionDetailsRequest } from '@ddt-web/pas';
import { PasDataLoaderService } from '@ddt-web/setup-data-workers';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { Config, StoreEntity, StoreService } from '@ddt-web/store-service';
import { MeQueryParamKey, UrlService } from '@ddt-web/url';
import { SecurityIdentifierTransformUtil } from '@ddt-web/wealth-web';
import * as difference from 'lodash/difference';
import * as flatten from 'lodash/flatten';
import * as get from 'lodash/get';
import * as groupBy from 'lodash/groupBy';
import * as has from 'lodash/has';
import * as isEmpty from 'lodash/isEmpty';
import * as omit from 'lodash/omit';
import * as set from 'lodash/set';
import * as values from 'lodash/values';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, first, map, mergeMap, switchMap } from 'rxjs/operators';

import { ModelsBrowseStoreConfig } from '../config/store.config';
import { ErrorCodes } from '../enums';
import { EsgModelData, ModelPortfolio } from '../models';
import { FamiliesUtil } from '../utils';
import { AssetPathUtil } from '../utils/asset-path.util';
import { ModelsBrowseApiService } from './models-browse-api.service';
import { ModelsBrowseStoreHelperService } from './models-browse-store-helper.service';
@Injectable({
	providedIn: 'root'
})
export class ModelsBrowseDataService {
	@InjectQueryParam(MeQueryParamKey.SUB_TYPE) private readonly subType: string;
	@InjectConfig(ModelsExperienceConfigKey.MODEL_FAMILIES_V2_1_API_ENABLE) private readonly isModelFamiliesV21Enable: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.BENCHMARK_ESG_SCORE) private readonly benchmarkEsgScore = {};
	@InjectConfig(ModelsExperienceConfigKey.ESG_SCORE) private readonly esgScore = {};
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;
	@InjectConfig(ModelsExperienceConfigKey.CACHE_BUSTER_MINUTES) private readonly cacheBusterMinutes: number = 15;
	@InjectConfig(ModelsExperienceConfigKey.MODELS_PUBLIC_API_TS_PATH) private readonly modelsPublicPath: string = '/productdata/BLKPublicModels.json';
	@InjectConfig(ModelsExperienceConfigKey.MODELS_PUBLIC_API_TS_PATH_PREVIEW) private readonly modelsPublicPathPreview: string = '/productdata/BLKPublicModels_preview.json';

	constructor(
		private modelsBrowseApiService: ModelsBrowseApiService,
		private modelsBrowseStoreHelperService: ModelsBrowseStoreHelperService,
		private storeService: StoreService,
		private urlService: UrlService,
		private contentService: ContentService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private pasDataLoaderService: PasDataLoaderService,
		private pasRcResolver: PasRcResolverService,
		private localeUtilResolverService: LocaleUtilResolverService
	) {
	}
	/**
	 * Method to fetch model families from the iRetire server
	 * @param modelBrowseConfigStore 
	 */
	public fetchOldFamilyList<T>(modelBrowseConfigStore: Config<T>): void {
		let fetchFamiliesApi = this.modelsBrowseApiService.fetchFamiliesFromIretireServer();

		if (this.isModelFamiliesV21Enable) {
			fetchFamiliesApi = this.modelsBrowseApiService.fetchFamiliesFromIretireServerV21();
		}

		fetchFamiliesApi.pipe(first()).subscribe(
			responseJson => {
				const isValidResponse = this.isValidAPIResponse(responseJson);
				const response = isValidResponse ? this.flattenAllDataResponse(responseJson.response) : [];
				// ordering the portfolios within each family in ascending order on basis of das model order entity
				response.forEach(family => {
					const familyPortfolios: Array<any> = family['portfolios'] ?? [];
					delete family['portfolios']; // removing portfolios key from the family object
					if (familyPortfolios.length) {
						family['models'] = ModelsTransformUtil.sortModelPortfolios(familyPortfolios, 'dasModelOrder'); // models index added to make it compatible to the config changes done for PAS Server.
					}
				});
				this.modelsBrowseStoreHelperService.updateStoreFromResponseList(modelBrowseConfigStore, response);
			},
			error => {
				this.storeService.upsertMany(modelBrowseConfigStore, []);
				throw new Error(ErrorCodes.NO_FAMILY_LIST);
			}
		);
	}

	/**
	 * Fetch list of families from iretire server and updates 'families' store, called on app load
	 *
	 * @template T
	 * @param {Config<T>} modelBrowseConfigStore
	 * @param {boolean} [publicView=false]
	 * @memberof ModelsBrowseDataService
	 */
	public fetchFamilyList<T>(modelBrowseConfigStore: Config<T>, publicView: boolean = false): void {
		if (publicView) {
			const publicUrl = AssetPathUtil.getAssetPath(this.cacheBusterMinutes, this.modelsPublicPath, this.modelsPublicPathPreview);
			this.modelsBrowseApiService.fetchFamiliesInPublicMode(publicUrl).pipe(first(), map(mapModelsFamiliesResponse)).subscribe(
				responseJson => {
					this.updateFamiliesStoreWithResponse(responseJson, modelBrowseConfigStore);
				},
				error => {
					this.storeService.upsertMany(modelBrowseConfigStore, []);
					throw new PasModelFamiliesError(error.error['error-id'], error.error.errorDescription, ERROR_SEVERITY.ERROR);
				}
			);
		} else {
			const requestParams: V1ListModelFamilyRequest = PasApiParamUtil.createModelFamiliesParams([V1ModelType.Blk, V1ModelType.Custom]);
			const locale = this.localeUtilResolverService.getResolvedLocaleValue();
			this.pasRcResolver.getRcForUserType(PasDataServiceApiConstants.MODEL_FAMILIES, locale).pipe(
				first(),
				map((rc: string) => ({ ...requestParams, rc })),
				switchMap((requestParams) => this.modelsBrowseApiService.fetchFamilies(requestParams))
			).subscribe(
				responseJson => {
					this.updateFamiliesStoreWithResponse(responseJson, modelBrowseConfigStore);
				},
				error => {
					this.storeService.upsertMany(modelBrowseConfigStore, []);
					throw new PasModelFamiliesError(error.error['error-id'], error.error.errorDescription, ERROR_SEVERITY.ERROR);
				}
			);
		}
	}

	/**
	 * Fetch holdings from API only if models for familyId are not present in 'familyHoldings' store.
	 * Return modelIds(to prepare component data for holdings table body) as-
	 *     a) when modelsIds is present in store
	 * 	   b) when modelsIds is not present in store
	 * @template T
	 * @param {string} familyId
	 * @returns {Observable<Observable<Array<string>>>}
	 * @memberof ModelsBrowseDataService
	 */
	public fetchFamilyHoldings<T>(familyId: string, skipLoadingState: boolean = false): Observable<Array<ModelPortfolio>> {
		return combineLatest([
			this.storeService.selectAll(ModelsBrowseStoreConfig.families),
			this.storeService.selectAll(ModelsBrowseStoreConfig.familyHoldings)
		]).pipe(
			first(([families, familyHolding]) =>
				!families.isLoading && !familyHolding.isLoading && !isEmpty(get(families.entities, familyId))),
			map(([families, familyHoldings]) => {
				const modelIdsInFamiliesStore = families.entities[familyId]?.models?.map(model => model.id);
				const subType = families.entities[familyId]?.subType || this.subType;

				if (!isEmpty(modelIdsInFamiliesStore)) {
					const modelIdsForFamilyId = difference(modelIdsInFamiliesStore, familyHoldings.ids);
					// If modelIds are not present in familyHoldings store, hit holdings api and update holdings store
					if (!isEmpty(modelIdsForFamilyId)) {
						return this.fetchFamilyHoldingsFromApi(modelIdsInFamiliesStore, subType, skipLoadingState).pipe(
							first(familyHoldings => !isEmpty(familyHoldings)),
							switchMap(familyHoldings => {
								const uniqueSecurities = SecurityIdentifierTransformUtil.createSecurityIdentifiersMapRequest(familyHoldings, false);
								const locale = this.localeUtilResolverService.getResolvedLocaleValue();
								return this.pasDataLoaderService.populatePositionDetailsForModelsHoldings({ securities: uniqueSecurities }, familyHoldings, locale).pipe(
									map(() => familyHoldings)
								);
							})
						);
					} else {
						// return array of models to fetch modelIds and asOfDate
						const modelsDataFromHoldingsStore = values(familyHoldings.entities);
						// Only filter data specific to that family ID, since familyHoldings store could contain models for other families
						const orderedModels = this.getOrderedModelsData(modelIdsInFamiliesStore, modelsDataFromHoldingsStore);

						// Check if position details need to be populated or retriggered
						return this.storeService.selectAll(ModelsBrowseStoreConfig.positionDetails).pipe(
							first(),
							switchMap(positionDetailsStore => {
								const needsPopulation = orderedModels.some(model => {
									const positionDetail = positionDetailsStore.entities[model.id];
									// Check if position details don't exist or are incomplete
									return !positionDetail || isEmpty(positionDetail.children);
								});

								if (needsPopulation) {
									// Retrigger position details population if not complete
									const uniqueSecurities = SecurityIdentifierTransformUtil.createSecurityIdentifiersMapRequest(orderedModels, false);
									const locale = this.localeUtilResolverService.getResolvedLocaleValue();
									return this.pasDataLoaderService.populatePositionDetailsForModelsHoldings({ securities: uniqueSecurities }, orderedModels, locale).pipe(
										map(() => orderedModels)
									);
								}

								return of(orderedModels);
							})
						);
					}
				}

				// returns empty list if no model ids are available
				return of([]);
			}),
			mergeMap(holdings => holdings),
			catchError(err => {
				//  TODO swchopra: Error Handling
				console.error(err.message || err);
				return of([]);
			})
		);
	}

	public fetchFamilyHoldingsForAllModels<T>(): Observable<Array<ModelPortfolio>> {
		return combineLatest([
			this.storeService.selectAll(ModelsBrowseStoreConfig.families),
			this.storeService.selectAll(ModelsBrowseStoreConfig.familyHoldings)
		]).pipe(
			first(([families, familyHoldings]) =>
				!families.isLoading && !familyHoldings.isLoading),
			map(([families, familyHoldings]) => {
				const apiToBeCalled: Observable<ModelPortfolio[]>[] = [];
				const allOrderedModels = [];
				families.ids?.forEach(familyId => {
					const modelIdsInFamiliesStore = families.entities[familyId]?.models?.map(model => model.id);
					const subType = families.entities[familyId]?.subType || this.subType;

					if (!isEmpty(modelIdsInFamiliesStore)) {
						const modelIdsForFamilyId = difference(modelIdsInFamiliesStore, familyHoldings.ids);
						// If modelIds are not present in familyHoldings store, hit holdings api and update holdings store
						if (!isEmpty(modelIdsForFamilyId)) {
							apiToBeCalled.push(this.fetchFamilyHoldingsFromApi(modelIdsInFamiliesStore, subType, false));
						} else {
							// return array of models to fetch modelIds and asOfDate
							const modelsDataFromHoldingsStore = values(familyHoldings.entities);
							// Only filter data specific to that family ID, since familyHoldings store could contain models for other families
							const orderedModels = this.getOrderedModelsData(modelIdsInFamiliesStore, modelsDataFromHoldingsStore);
							allOrderedModels.push(...orderedModels);
						}
					}
				});
				if (!isEmpty(apiToBeCalled)) {
					return forkJoin(apiToBeCalled).pipe(
						map(([...allOrderedModelsFromApi]) => {
							return [...flatten(allOrderedModelsFromApi), ...allOrderedModels];
						}),
						switchMap((modelHoldings) => {
							const uniqueSecurities = SecurityIdentifierTransformUtil.createSecurityIdentifiersMapRequest(modelHoldings);
							const locale = this.localeUtilResolverService.getResolvedLocaleValue();
							return this.pasDataLoaderService.populatePositionDetailsForModelsHoldings({ securities: uniqueSecurities }, modelHoldings, locale).pipe(
								map(() => modelHoldings)
							);
						})
					);
				} else {
					return of(allOrderedModels);
				}
			}),
			mergeMap(holdings => holdings),
			catchError(err => {
				console.error(err.message || err);
				return of([]);
			})
		);
	}

	/**
	 * Checks if the familyPerformance feature store has performance data for the familyId in URL query params.
	 * If performance data is in the store, it simply returns the familyId as observable.
	 * If performance data is not in the store, it returns the performance data from the API
	 * skipLoadingState can also be specify if we want to make an API call without switching the isLoading flag for UX purpose
	 *
	 * @memberof ModelsBrowseDataService
	 * @param {string} familyId
	 * @param {boolean} skipLoadingState
	 * @returns {Observable<string>}
	 */
	public fetchFamilyPerformance(familyId: string, skipLoadingState: boolean = false): Observable<string> {
		return combineLatest([
			this.storeService.selectById(ModelsBrowseStoreConfig.familyPerformance, familyId),
			this.storeService.selectById(ModelsBrowseStoreConfig.familyCustomNetPerformance, familyId),
			this.applicationStateStoreProxyService.getPerformanceState()
		])
			.pipe(
				first(),
				map(([familyPerformance, familyCustomNetPerformance, state]) => {
					const isValidFamilyId = !!familyId;
					const isPerformanceLoadedForFamily = isValidFamilyId && !!familyPerformance && !familyPerformance?.[familyId]?.error;
					const appStateCustomAdvisoryFee = state?.customAdvisoryFee;
					const familyCustomAdvisoryFee = familyCustomNetPerformance?.[familyId]?.customAdvisoryFee;
					const hasCustomFeeChangesForFamily = isPerformanceLoadedForFamily && appStateCustomAdvisoryFee && (familyCustomAdvisoryFee !== appStateCustomAdvisoryFee);
					if (isValidFamilyId && !isPerformanceLoadedForFamily || hasCustomFeeChangesForFamily) {
						return this.fetchFamilyPerformanceFromApi(familyId, state?.customAdvisoryFee, skipLoadingState);
					}
					else {
						return of(familyId);
					}
				}),
				mergeMap(familyId => familyId)
			);
	}

	/**
	 * @memberof ModelsBrowseDataService
	 * @param familyId 
	 * @returns {Observable<string>}
	 */
	public fetchFamilyModelsAnalytics(familyId: string, skipLoadingState: boolean = false): Observable<string> {
		return combineLatest([
			this.storeService.selectById(ModelsBrowseStoreConfig.families, familyId),
			this.storeService.selectById(ModelsBrowseStoreConfig.familyOverview, familyId),
			this.storeService.selectById(ModelsBrowseStoreConfig.familyPerformance, familyId),
			this.applicationStateStoreProxyService.getPerformanceState()
		])
			.pipe(
				first(),
				map(([family, familyOverview, familyPerformance, state]) => {
					const isValidFamilyId = !!familyId;
					const isModelsAnalyticsLoadedForFamily = isValidFamilyId && !!familyOverview && !familyOverview?.[familyId]?.error
						&& !!familyPerformance && !familyPerformance?.[familyId]?.error;
					const subType = family?.[familyId]?.subType || this.subType;
					const appStateCustomAdvisoryFee = state?.customAdvisoryFee;
					const familyCustomAdvisoryFee = familyPerformance?.[familyId]?.customAdvisoryFee;
					const hasCustomFeeChangesForFamily = isModelsAnalyticsLoadedForFamily && appStateCustomAdvisoryFee && (familyCustomAdvisoryFee !== appStateCustomAdvisoryFee);
					if (isValidFamilyId && !isModelsAnalyticsLoadedForFamily || hasCustomFeeChangesForFamily) {
						return this.fetchFamilyPerformanceFromApi(familyId, state?.customAdvisoryFee, skipLoadingState, subType);
					}
					return of(familyId);
				}),
				mergeMap(familyId => familyId)
			);
	}


	/**
	 * Fetches portfolio analysis for the selected family (provided in the URL query params)
	 * and returns the family ID which will have the fund perf information.
	 */
	public fetchFamilyPortfolioAnalysis<T>(familyIdentifier?: string): Observable<string> {
		const familyId = this.urlService.getModelFamilyId() || familyIdentifier;
		if (!familyId) {
			return of('');
		}

		return this.fetchFamilyHoldings(familyId)
			.pipe(
				first(familyHoldings => !isEmpty(familyHoldings)),
				switchMap(familyHoldings => {
					const uniqueSecurities = SecurityIdentifierTransformUtil.createSecurityIdentifiersMapRequest(familyHoldings);
					this.pasDataLoaderService.populatePositionDetailsForModels({ securities: uniqueSecurities } as SecurityResolutionDetailsRequest, familyId);
					return of(familyId);
				})
			);
	}

	/**
	 * Flattens the response by moving fields inside allData to the parent object
	 *
	 * @private
	 * @param {*} responseJson
	 * @returns {object}
	 * @memberof ModelsBrowseDataService
		 */
	public flattenAllDataResponse(responseArray: any[]): Array<any> {
		return responseArray.filter(response => response).map(response => {
			response = Object.assign({}, get(response, 'allData'), omit(response, 'allData'));
			if (has(response, 'portfolios')) {
				response.portfolios = this.flattenAllDataResponse(response.portfolios);
			}
			if (has(response, 'children')) {
				response.children = this.flattenAllDataResponse(response.children);
			}

			return response;
		});
	}

	public getInceptionDateText(families: StoreEntity<any>, familyHoldingsData: Array<ModelPortfolio>, familyId: string, selectedIds: string[] = []): string {
		// Get the short / long names of each model in the family
		const modelIdNames: { [modelId: string]: string } = {};
		get(families, [familyId, 'models'], []).forEach(model => {
			const modelName = model.shortName || model.description;
			//when risk profile modularity is enabled, only selected models should be included in inception date text
			const includeModelId: boolean = selectedIds.length ? selectedIds.includes(model.id) : true;
			if (model.id && modelName && includeModelId) {
				modelIdNames[model.id] = modelName;
			}
		});

		const modelsWithInceptionDate = familyHoldingsData.map(model =>
			Object.assign({}, { id: model.id, inceptionDate: model.metadata['inceptionDate'] })).filter(model => model?.inceptionDate); // Please verify the key name in batch get API response
		const inceptionDatesGrouped = groupBy(modelsWithInceptionDate, 'inceptionDate');
		const inceptionDates = Object.keys(inceptionDatesGrouped);
		const contentMapping = new Map();

		if (!inceptionDates.length) {
			return this.contentService.get('INCEPTION_DATE_ERROR', 'Cannot retrieve inception date');
		} else if (inceptionDates.length === 1) {
			const inceptionDate = inceptionDates[0];
			contentMapping.set('$INCEPTION_DATE$', new DateFormatter(this.dateFormat).format(inceptionDate));

			const dcrText = this.contentService.get('INCEPTION_DATE_ALL', 'The inception date for the model portfolios is <strong>$INCEPTION_DATE$</strong>.');
			return this.contentService.getSubstitutedValuesString(dcrText, contentMapping);
		} else if (inceptionDates.length > 1) {
			let inceptionDateText = '';
			inceptionDates.forEach(inceptionDate => {
				const models = inceptionDatesGrouped[inceptionDate];
				let modelNames;
				// TODO Waris: Check if this is needed
				// if (this.isAdamApiEnabled) {
				// modelNames = models.map(model => modelIdNames[model.portfolioId]).filter(model => !!model); // get model name for each model
				// } else {
				modelNames = models.map(model => modelIdNames[model.id]).filter(model => !!model); // get model name for each model
				// }

				contentMapping.set('$MODEL_NAME$', modelNames.join(', '));
				contentMapping.set('$INCEPTION_DATE$', new DateFormatter(this.dateFormat).format(inceptionDate));

				if (modelNames.length === 1) {
					const dcrText = this.contentService.get('INCEPTION_DATE_MANY_ONE', 'Inception date for the $MODEL_NAME$ model is <strong>$INCEPTION_DATE$</strong>.') + ' ';
					inceptionDateText += this.contentService.getSubstitutedValuesString(dcrText, contentMapping);
				}
				else if (modelNames.length > 1) {
					const dcrText = this.contentService.get('INCEPTION_DATE_MANY_MANY', 'Inception date for the $MODEL_NAME$ models is <strong>$INCEPTION_DATE$</strong>.') + ' ';
					inceptionDateText += this.contentService.getSubstitutedValuesString(dcrText, contentMapping);
				}
			});
			return inceptionDateText.trim();
		}
	}

	public checkPerformanceDataValidity(familyId: string, customAdvisoryFee: number): Observable<boolean> {
		return combineLatest([
			this.storeService.selectById(ModelsBrowseStoreConfig.familyPerformance, familyId)
		]).pipe(
			first(),
			map(([familyPerformance]) => {
				const performanceData = familyPerformance?.[familyId];
				const isPerformanceDataValid = !!performanceData && !performanceData?.error;
				const grossDate = performanceData?.models?.[0]?.performance?.exPostMetrics[0]?.interval?.stopDate; // Need to discuss within the team
				const netDate = performanceData?.models?.[0]?.performance?.netExPostMetrics[0]?.interval?.stopDate; // Need to discuss within the team
				const isDateMatch = !grossDate || !netDate || grossDate === netDate;
				return isPerformanceDataValid && (!customAdvisoryFee || isDateMatch);
			})
		);
	}

	public getFamilyNameId(familyId: string): Observable<string> {
		return this.storeService.selectAll(ModelsBrowseStoreConfig.families).pipe(
			first(families => !families.isLoading),
			map((families) => {
				return get(families.entities, [familyId, 'familyNameId']);
			}));
	}

	public getAladdinTicker(familyId: string): Observable<string> {
		return this.storeService.selectAll(ModelsBrowseStoreConfig.families).pipe(
			first(families => !families.isLoading),
			map((families) => {
				return get(families.entities, [familyId, 'aladdinTicker']);
			}));
	}
	/**
	 * 
	 * @param familyId 
	 * @returns 
	 */
	public getBenchmarkDisclaimer(familyId: string): Observable<string | undefined> {
		return this.storeService.selectAll(ModelsBrowseStoreConfig.families).pipe(
			first(families => !families.isLoading),
			map((families) => {
				return get(families.entities, [familyId, 'modelBenchmarkDisclaimer']);
			}));
	}
	/**
	 * 
	 * @returns 
	 */
	public isBlkModelsAvailable(): Observable<boolean> {
		return this.storeService.selectAll(ModelsBrowseStoreConfig.families).pipe(
			first(families => !families.isLoading),
			map((families) => {
				const { categories } = FamiliesUtil.generateCategoriesModelLevel(families);
				return categories.includes(SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP);
			}));
	}

	public fetchESGModelsAnalytics(familyId: string): Observable<EsgModelData> {
		return combineLatest([
			this.storeService.selectById(ModelsBrowseStoreConfig.familyEsg, familyId)
		])
			.pipe(
				first(),
				map(([familyEsg]) => {
					const subType = familyEsg?.[familyId]?.subType || this.subType;
					const isValidFamilyId = !!familyId;
					const isEsgAnalyticsLoadedForFamily = isValidFamilyId && !!familyEsg && !familyEsg?.[familyId]?.error;
					if (isValidFamilyId && !isEsgAnalyticsLoadedForFamily) {
						return this.fetchFamilyEsgDataFromApi(familyId, subType);
					}
					return of(familyEsg[familyId]);
				}),
				mergeMap(familyId => familyId)
			);
	}

	public fetchFamilyEsgDataFromApi(familyId: string, subType: string) {
		return this.modelsBrowseApiService.fetchFamilyModelsEsgAnalytics(familyId, subType)
			.pipe(
				first(),
				map(responseJson => {
					const modelPortfolioAnalytics = responseJson?.modelPortfolioAnalytics;
					const esgData = modelPortfolioAnalytics?.map(item => {
						const totalPortfolioAnalysis = item?.totalPortfolioAnalysis;
						return {
							id: totalPortfolioAnalysis?.id,
							esgScores: this.esgScore?.[totalPortfolioAnalysis?.id],
							benchmarkEsgScores: this.benchmarkEsgScore?.[totalPortfolioAnalysis?.id],
							analysisDate: totalPortfolioAnalysis?.analysisDate
						};
					});
					const familyEsgData = { id: familyId, models: esgData };
					this.modelsBrowseStoreHelperService.updateStoreFromResponse(ModelsBrowseStoreConfig.familyEsg, familyEsgData);
					return familyEsgData;
				}),
				catchError(error => {
					this.modelsBrowseStoreHelperService.updateStoreFromResponse(ModelsBrowseStoreConfig.familyEsg, {
						id: familyId,
						error: ErrorCodes.INVALID_FAMILY_ESG_RESPONSE
					});
					console.error(error.message || error);
					return of(null);
				})
			);
	}

	/**
	 * Order models in latest holdings and changes to holdings tab as per families store models order
	 *
	 * @private
	 * @param {Array<string>} modelIdsInFamiliesStore
	 * @param {Array<ModelPortfolio>} modelsDataFromHoldingsStore
	 * @return {*}  {Array<ModelPortfolio>}
	 * @memberof ModelsBrowseDataService
	 */
	private getOrderedModelsData(modelIdsInFamiliesStore: Array<string>, modelsDataFromHoldingsStore: Array<ModelPortfolio>): Array<ModelPortfolio> {
		const orderedModelsData: Array<ModelPortfolio> = [];
		modelIdsInFamiliesStore.forEach(modelId => {

			const model = modelsDataFromHoldingsStore.find(holding => (holding.id === modelId));

			if (!isEmpty(model)) {
				// check if the models are on-boarded for the first time
				const missingPrevApprovedHoldings = model.metadata?.['missingPrevApprovedHoldings'] === 'true';
				// add/update this flag if models not rebalanced and no changes to holdings available
				set(model, 'noPreviousHoldings', missingPrevApprovedHoldings);
				orderedModelsData.push(model);
			}
		});
		return orderedModelsData;
	}

	/**
	* Check if API response is valid
	*
	* @param {*} responseJson
	* @returns {boolean}
	* @memberof ModelsBrowseDataService
	*/
	private isValidAPIResponse(responseJson: any, familyId?: string): boolean {
		const response = familyId ? get(responseJson, ['response', familyId], []) : get(responseJson, 'response', []);
		return get(responseJson, 'success') && !isEmpty(response);
	}

	/**
	 * Fetch holdings for all models of family from iretire server
	 * & updates 'familyHoldings' store with models holdings data
	 *
	 * @template T
	 * @param {string} modelIdsInFamiliesStore
	 * @memberof ModelsBrowseDataService
	 */
	private fetchFamilyHoldingsFromApi<T>(modelIdsInFamiliesStore: Array<string>, subType: string, skipLoadingState: boolean = false): Observable<Array<ModelPortfolio>> {
		if (!skipLoadingState) {
			this.storeService.fetch(ModelsBrowseStoreConfig.familyHoldings);
		}
		return this.modelsBrowseApiService.fetchFamilyHoldings(modelIdsInFamiliesStore, subType).pipe(
			map(responseJson => {
				const modelList = responseJson.models || [];

				// Ordering of models in holdings store as per the families store modelIds
				const orderedModelsData = this.getOrderedModelsData(modelIdsInFamiliesStore, modelList);

				this.modelsBrowseStoreHelperService.updateStoreFromResponseList(ModelsBrowseStoreConfig.familyHoldings, orderedModelsData);
				return orderedModelsData;
			}),
			catchError(err => {
				console.error(err.message || err);
				return of([]);
			}),
			finalize(() => {
				// Reset loading state when observable completes or is cancelled
				// This ensures subsequent calls don't block waiting for isLoading to become false
				if (!skipLoadingState) {
					this.storeService.noAction(ModelsBrowseStoreConfig.familyHoldings);
				}
			})
		);
	}

	/**
		 * Fetch performance data of family from iretire server
		 * & updates following store with models performance and models performance benchmark
		 * 'familyPerformance' for gross performance and benchmark
		 * 'familyNetPerformance' for net performance with default (3%) advisory fees
		 * 'familyNetCustomPerformance ' for net performance with custom advisory fee (if set by user)
		 * If the API results in an error or the response in invalid, an error is thrown
		 * and the store is updated with empty object
		 * Custom fee and skipLoadingState flag will be provided from parent method only
		 *
		 * @param {string} familyId
		 * @param {number} customAdvisoryFee
		 * @param {boolean} skipLoadingState
		 * @returns {Observable<string>}
		 * @memberof ModelsBrowseDataService
		 */
	private fetchFamilyPerformanceFromApi(familyId: string, customAdvisoryFee?: number, skipLoadingState: boolean = false, subType: string = ''): Observable<string> {
		if (!skipLoadingState) {
			this.storeService.fetch(ModelsBrowseStoreConfig.familyOverview);
			if (customAdvisoryFee) {
				this.storeService.fetch(ModelsBrowseStoreConfig.familyPerformance);
			}
		}
		return this.modelsBrowseApiService.fetchFamilyModelsAnalytics(familyId, customAdvisoryFee, subType)
			.pipe(
				first(),
				map(responseJson => {
					const modelPortfolioAnalytics = responseJson.modelPortfolioAnalytics;
					const modelsData = modelPortfolioAnalytics.map(item => {
						const totalPortfolioAnalysis = item.totalPortfolioAnalysis;
						//TODO rohyadav: will be after the data mappings are updated.
						const overviewData = {
							id: totalPortfolioAnalysis.id, risk: totalPortfolioAnalysis.portfolioAnalysis.risk,
							characteristics: totalPortfolioAnalysis.portfolioAnalysis.characteristics, analysisDate: totalPortfolioAnalysis.analysisDate
						};
						const performanceData = {
							id: totalPortfolioAnalysis.id, performance: totalPortfolioAnalysis.portfolioAnalysis.performance, analysisDate: totalPortfolioAnalysis.analysisDate
						};
						const benchmarkData = {
							id: totalPortfolioAnalysis.id, benchmarkAnalysis: totalPortfolioAnalysis.benchmarkAnalysis, benchMarkId: totalPortfolioAnalysis.benchmarkId,
							analysisDate: totalPortfolioAnalysis.analysisDate
						};
						return { overviewData, performanceData, benchmarkData };
					});
					const familyModelsOverViewData = { id: familyId, models: modelsData.map(item => item.overviewData) };
					this.modelsBrowseStoreHelperService.updateStoreFromResponse(ModelsBrowseStoreConfig.familyOverview, familyModelsOverViewData);
					const familyPerformance = {
						models: modelsData.map(item => item.performanceData),
						modelsBenchmark: modelsData.map(item => item.benchmarkData),
						id: familyId,
						customAdvisoryFee: (modelPortfolioAnalytics[0].totalPortfolioAnalysis.portfolioAnalysis.performance.performanceFee * 100) // Storing in store in percent unit
					};
					this.modelsBrowseStoreHelperService.updateStoreFromResponse(ModelsBrowseStoreConfig.familyPerformance, familyPerformance);
					return modelsData.map(item => item.overviewData).length;

				}),
				catchError(error => {
					this.modelsBrowseStoreHelperService.updateStoreFromResponse(ModelsBrowseStoreConfig.familyOverview, {
						id: familyId,
						error: ErrorCodes.INVALID_FAMILY_OVERVIEW_RESPONSE
					});
					this.modelsBrowseStoreHelperService.updateStoreFromResponse(ModelsBrowseStoreConfig.familyPerformance, {
						id: familyId,
						error: ErrorCodes.INVALID_FAMILY_PERFORMANCE_RESPONSE
					});
					console.error(error.message || error);
					if (error.error) {
						throw new ErrorObject(error.error, ModelsBrowseStoreConfig.familyPerformance.name);
					}
					return of(null);
				})
			);
	}

	private updateFamiliesStoreWithResponse<T>(responseJson: any, modelBrowseConfigStore: Config<T>): void {
		const response = responseJson.modelFamilies;
		if (response.length) {
			// ordering the portfolios within each family in ascending order on basis of das model order entity
			response.forEach(family => {
				const familyPortfolios: Array<any> = family['models'] ?? [];
				if (familyPortfolios.length) {
					family['models'] = ModelsTransformUtil.sortModelPortfolios(familyPortfolios, 'dasModelOrder');
				}
			});
			this.modelsBrowseStoreHelperService.updateStoreFromResponseList(modelBrowseConfigStore, response);
		}
		if (responseJson.error) {
			const errorSeverity = response.length ? ERROR_SEVERITY.WARNING : ERROR_SEVERITY.CRITICAL; // response array has some families missing then error severity is warning else its critical
			throw new PasModelFamiliesError(responseJson.error.code, responseJson.error.message, errorSeverity);
		}

	}
}
